import React, { useState } from 'react';
import { getAdminBadge } from '../../utils/getAdminBadge';

const StatTile = ({
  admin = false,
  value,
  label,
  icon,
  color = 'blue',
  subValue = null,
  previousValue = null,
  size = 'm',
  className = '',
  additionalInfo = null,
  onClick = null,
  variation: propVariation,
  difference: propDifference,
  description = null
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Réutiliser la même logique de couleurs que DNOverview
  const getColorClasses = (color) => {
    const colors = {
      green: 'bg-green-100 border-green-300 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300',
      lime: 'bg-lime-100 border-lime-300 text-lime-800 dark:bg-lime-900/30 dark:border-lime-700 dark:text-lime-300',
      yellow: 'bg-yellow-100 border-yellow-300 text-yellow-800 dark:bg-yellow-900/30 dark:border-yellow-700 dark:text-yellow-300',
      orange: 'bg-orange-100 border-orange-300 text-orange-800 dark:bg-orange-900/30 dark:border-orange-700 dark:text-orange-300',
      red: 'bg-red-100 border-red-300 text-red-800 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300',
      blue: 'bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300',
      indigo: 'bg-indigo-100 border-indigo-300 text-indigo-800 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300',
      purple: 'bg-purple-100 border-purple-300 text-purple-800 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300',
      teal: 'bg-teal-100 border-teal-300 text-teal-800 dark:bg-teal-900/30 dark:border-teal-700 dark:text-teal-300',
    };
    return colors[color] || colors.blue;
  };

  const getIconColorClasses = (color) => {
    const colors = {
      green: 'text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-200',
      lime: 'text-lime-500 hover:text-lime-700 dark:text-lime-400 dark:hover:text-lime-200',
      yellow: 'text-yellow-500 hover:text-yellow-700 dark:text-yellow-400 dark:hover:text-yellow-200',
      orange: 'text-orange-500 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-200',
      red: 'text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200',
      blue: 'text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-200',
      indigo: 'text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-200',
      purple: 'text-purple-500 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-200',
      teal: 'text-teal-500 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-200',
    };
    return colors[color] || colors.blue;
  };

  const getSizeClasses = (size) => {
    const sizes = {
      s: {
        tile: 'h-18 py-1.5 px-2',
        icon: 'h-[20px] w-[20px] mb-0.5',
        label: 'text-[10px] mb-0.5',
        value: 'text-lg',
        subValue: 'text-xs',
        variation: 'text-xs',
        additionalInfo: 'text-[8px]'
      },
      m: {
        tile: 'h-32 py-4 px-3',
        icon: 'h-6 w-6 mb-1',
        label: 'text-sm mb-1',
        value: 'text-2xl',
        subValue: 'text-xs',
        variation: 'text-sm',
        additionalInfo: 'text-xs'
      },
      l: {
        tile: 'h-40 py-5 px-4',
        icon: 'h-8 w-8 mb-3',
        label: 'text-base mb-2',
        value: 'text-3xl',
        subValue: 'text-sm',
        variation: 'text-base',
        additionalInfo: 'text-sm'
      }
    };
    return sizes[size] || sizes.m;
  };

  // Extraire les valeurs numériques
  const extractNumericValue = (val) => {
    if (val === null || val === undefined) return null;

    // Si la valeur contient un %, tenter d'extraire le nombre
    if (typeof val === 'string') {
      const numVal = parseFloat(val.replace(/[^\d.-]/g, ''));
      return isNaN(numVal) ? val : numVal;
    }

    return parseFloat(val);
  };

  // Calcul de la variation en pourcentage
  const calculateVariation = () => {
    if (previousValue === null || isNaN(parseFloat(value)) || isNaN(parseFloat(previousValue))) {
      return null;
    }

    // Extraire les nombres des valeurs (au cas où ils contiennent des caractères comme %)
    const currentVal = parseFloat(value.toString().replace(/[^\d.-]/g, ''));
    const prevVal = parseFloat(previousValue.toString().replace(/[^\d.-]/g, ''));

    if (prevVal === 0) {
      return currentVal > 0 ? '+∞' : '0';
    }

    const variation = ((currentVal - prevVal) / Math.abs(prevVal)) * 100;

    // Formater avec 1 ou 2 chiffres après la virgule selon le besoin
    let formattedVariation;

    if (Math.abs(variation) < 10) {
      // Pour les petites variations, montrer 2 décimales
      formattedVariation = variation.toFixed(2);
    } else {
      // Pour les grandes variations, une seule décimale suffit
      formattedVariation = variation.toFixed(1);
    }

    // Supprimer les zéros non significatifs à la fin (.00 ou .x0)
    formattedVariation = parseFloat(formattedVariation).toString();

    return variation > 0
      ? `+${formattedVariation}%`
      : `${formattedVariation}%`;
  };

  // Calculer la différence numérique absolue
  const calculateNumericDifference = () => {
    if (previousValue === null || isNaN(parseFloat(value)) || isNaN(parseFloat(previousValue))) {
      return null;
    }

    const currentVal = extractNumericValue(value);
    const prevVal = extractNumericValue(previousValue);

    if (prevVal === null || currentVal === null) return null;

    const diff = currentVal - prevVal;

    // Formater avec 1 ou 2 chiffres après la virgule selon le besoin
    let formattedDiff;

    if (Math.abs(diff) < 10) {
      // Pour les petites différences, montrer 2 décimales
      formattedDiff = diff.toFixed(2);
    } else {
      // Pour les grandes différences, une seule décimale suffit
      formattedDiff = diff.toFixed(1);
    }

    // Supprimer les zéros non significatifs à la fin (.00 ou .x0)
    formattedDiff = parseFloat(formattedDiff).toString();

    const prefix = diff > 0 ? '+' : '';
    return `${prefix}${formattedDiff}`;
  };

  const variation = propVariation !== undefined ? propVariation : calculateVariation();
  const numericDifference = propDifference !== undefined ? propDifference : calculateNumericDifference();
  const sizeClasses = getSizeClasses(size);

  // Déterminer la couleur de la variation
  const getVariationColor = () => {
    if (!variation) return '';

    const variationValue = parseFloat(variation);

    if (variationValue > 0) return 'text-green-600 dark:text-green-400';
    if (variationValue < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-500 dark:text-gray-400';
  };

  return (
    <div
      className={`
        w-full border rounded-lg transition-all hover:shadow-md relative
        ${getColorClasses(color)}
        ${sizeClasses.tile}
        flex flex-col items-center justify-center
        ${className}
        ${onClick ? 'cursor-pointer' : ''}
        ${showTooltip ? 'z-20' : ''}
      `}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {description && (
        <div
          className="absolute top-2 right-2"
          onClick={(e) => {
            e.stopPropagation();
            setShowTooltip(!showTooltip);
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 cursor-help transition-colors ${getIconColorClasses(color)}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>

          {showTooltip && (
            <div className="absolute right-0 bottom-full mb-1 w-48 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-50 pointer-events-none text-left">
              {description}
              <div className="absolute top-full right-1 -mt-1 border-4 border-transparent border-t-gray-800"></div>
            </div>
          )}
        </div>
      )}
      {icon && (
        <div className={sizeClasses.icon}>
          {React.cloneElement(icon, {
            className: `${sizeClasses.icon} ${icon.props.className || ''}`
          })}
        </div>
      )}
      <div className={`${sizeClasses.label} font-semibold text-center break-words w-full px-1`}>
        {label} {admin && getAdminBadge()}
      </div>
      <div className="flex flex-col items-center">
        <span className={`${sizeClasses.value} font-bold`}>
          {typeof value === 'string' && !isNaN(parseInt(value)) && parseInt(value) > 1000
            ? parseInt(value).toLocaleString('fr-FR')
            : typeof value === 'number' && value > 1000
              ? value.toLocaleString('fr-FR')
              : value}
        </span>
        {variation && (
          <span className={`${sizeClasses.variation} font-medium ${getVariationColor()} flex items-center mt-1`}>
            {numericDifference.includes('+') || numericDifference.includes('-') ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d={numericDifference.includes('+')
                  ? "M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
                  : "M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"}
                  clipRule="evenodd" />
              </svg>
            ) : null}
            {isHovered && variation ? variation : numericDifference}
          </span>
        )}
        {subValue && (
          <span className={`${sizeClasses.subValue} opacity-80 mt-0.5`}>{subValue}</span>
        )}
      </div>
      {additionalInfo && (
        <div className={`${sizeClasses.additionalInfo} text-center mt-1 opacity-70`}>
          {additionalInfo}
        </div>
      )}
    </div>
  );
};

export default StatTile;
